import React, { useState } from 'react';

interface PlusMinusButtonProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
}

/**
 * Custom plus-minus button component with left-right -+ layout and manual input
 */
const PlusMinusButton: React.FC<PlusMinusButtonProps> = ({
  value,
  onValueChange,
  min = 0,
  max = Infinity,
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value.toString());

  // Handle input change - only allow numbers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(newValue) || newValue === '') {
      setInputValue(newValue);
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    let numValue = Number(inputValue || value);
    if (isNaN(numValue)) numValue = value;
    if (numValue < min) numValue = min;
    if (numValue > max) numValue = max;
    setInputValue(numValue.toString());
    onValueChange(numValue);
  };

  // Handle keyboard enter
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  // Handle increment
  const handleIncrement = () => {
    const newValue = Math.min(value + 1, max);
    setInputValue(newValue.toString());
    onValueChange(newValue);
  };

  // Handle decrement
  const handleDecrement = () => {
    const newValue = Math.max(value - 1, min);
    setInputValue(newValue.toString());
    onValueChange(newValue);
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Minus button on left */}
      <button
        className="w-8 h-8 bg-gray-700 hover:bg-gray-600 rounded flex items-center justify-center text-white transition-colors"
        onClick={handleDecrement}
        disabled={value <= min}
        aria-label="Decrease"
      >
        -
      </button>

      {/* Manual input field as text to completely avoid spin buttons */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyPress={handleKeyPress}
          className="w-12 h-8 bg-gray-800/80 text-center text-sm text-white rounded border border-gray-700/50 px-1 py-1 focus:outline-none focus:ring-blue-500/50"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
        />
      </div>

      {/* Plus button on right */}
      <button
        className="w-8 h-8 bg-gray-700 hover:bg-gray-600 rounded flex items-center justify-center text-white transition-colors"
        onClick={handleIncrement}
        disabled={value >= max}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
};

export default PlusMinusButton;
export type { PlusMinusButtonProps };